// -----------------------------------------------------------------------------
// shared/time.ts — time, timecode and constant-frame-rate (CFR) helpers.
//
// The whole editor works in SECONDS as the canonical unit (clip positions,
// durations, the playhead). These helpers convert to/from frame indices for a
// given fps, and format seconds as human timecode for the ruler and readouts.
// -----------------------------------------------------------------------------

export function clamp(value: number, lo: number, hi: number): number {
  return value < lo ? lo : value > hi ? hi : value;
}

// Round a time to the nearest whole frame for the given fps. Keeps everything on
// a constant-frame-rate grid so export timestamps line up exactly.
export function snapToFrame(timeSec: number, fps: number): number {
  return Math.round(timeSec * fps) / fps;
}

export function timeToFrame(timeSec: number, fps: number): number {
  return Math.round(timeSec * fps);
}

export function frameToTime(frame: number, fps: number): number {
  return frame / fps;
}

// Format seconds as M:SS.cs (e.g. 1:07.40) for compact readouts.
export function formatTimecode(timeSec: number): string {
  const sign = timeSec < 0 ? '-' : '';
  const t = Math.abs(timeSec);
  const minutes = Math.floor(t / 60);
  const seconds = Math.floor(t % 60);
  const centis = Math.floor((t - Math.floor(t)) * 100);
  return `${sign}${minutes}:${pad2(seconds)}.${pad2(centis)}`;
}

// Format seconds as HH:MM:SS:FF (frames) for the timeline ruler.
export function formatTimecodeFrames(timeSec: number, fps: number): string {
  const t = Math.max(0, timeSec);
  const totalFrames = Math.round(t * fps);
  const f = totalFrames % Math.round(fps);
  const totalSeconds = Math.floor(totalFrames / fps);
  const s = totalSeconds % 60;
  const m = Math.floor(totalSeconds / 60) % 60;
  const h = Math.floor(totalSeconds / 3600);
  return `${pad2(h)}:${pad2(m)}:${pad2(s)}:${pad2(f)}`;
}

function pad2(n: number): string {
  return n < 10 ? '0' + n : String(n);
}
